import React, { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Link
} from "react-router-dom";
import "../App.css";
import axios from "axios";
import {
  ArrowRight, ShieldCheck, Star, Check, FileText, Wallet,
  TrendingUp, Building2, Receipt, AlertCircle, Briefcase,
  MessageCircle, Phone, Mail, Clock, Plus, Award, Users,
  Zap, Lock, ChevronDown, Calculator
} from "lucide-react";

// import adBanner1 from "../public/imgs/adbanner.png";
// import adBanner2 from "../public/imgs/adbanner1.png";
// import adBanner3 from "../public/imgs/adbanner2.png";
import AdminDashboard from "./AdminDashboard";
import Login from "./Login";

const banners = [
  "/imgs/adbanner.webp",
  "/imgs/adbanner1.webp",
  "/imgs/adbanner2.webp",
];
const randomBanner = banners[Math.floor(Math.random() * banners.length)];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:10000";
const API = `${BACKEND_URL}/api`;
const WHATSAPP = "917390090042";
const WA_MSG = "Hi! I want to file my ITR with IncomeTaxBro. Please help me.";

// ── Tax Logic AY 2026-27 ──
const newSlabs = [[400000,0],[800000,.05],[1200000,.10],[1600000,.15],[2000000,.20],[2400000,.25],[Infinity,.30]];
const oldSlabs = [[250000,0],[500000,.05],[1000000,.20],[Infinity,.30]];

const getSurcharge = (tax, ti, regime) => {
  if (ti <= 5000000)  return 0;
  if (ti <= 10000000) return tax * .10;
  if (ti <= 20000000) return tax * .15;
  if (ti <= 50000000) return tax * .25;
  return regime === "new" ? tax * .25 : tax * .37;
};

const computeTax = (income, regime, deductions = 0, age = "normal") => {
  const std = regime === "new" ? 75000 : 50000;
  let exempt = regime === "old" ? (age === "senior" ? 300000 : age === "supersenior" ? 500000 : 250000) : 0;
  let taxable = Math.max(0, income - std);
  if (regime === "old") taxable = Math.max(0, taxable - deductions);
  let tax = 0, prev = 0;
  if (regime === "old") {
    if (taxable <= exempt) { tax = 0; }
    else {
      prev = exempt;
      for (const [cap, rate] of [[500000,.05],[1000000,.20],[Infinity,.30]]) {
        if (taxable > cap) { tax += (cap - prev) * rate; prev = cap; }
        else { tax += (taxable - prev) * rate; break; }
      }
    }
  } else {
    for (const [cap, rate] of newSlabs) {
      if (taxable > cap) { tax += (cap - prev) * rate; prev = cap; }
      else { tax += (taxable - prev) * rate; break; }
    }
  }
  tax = Math.max(0, tax);
  if (regime === "new" && taxable <= 1200000) tax = 0;
  if (regime === "old" && taxable <= 500000)  tax = 0;
  const surcharge = getSurcharge(tax, taxable, regime);
  const cess = (tax + surcharge) * .04;
  return {
    taxable,
    tax:       Math.max(0, tax),
    surcharge: Math.max(0, surcharge),
    cess:      Math.max(0, cess),
    total:     Math.max(0, tax + surcharge + cess),
  };
};

const fmtINR = n => {
  const s = Math.round(n).toString();
  if (s.length <= 3) return "₹" + s;
  const l = s.slice(-3), r = s.slice(0, -3);
  return "₹" + r.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + l;
};

// ── Static Data ──
const services = [
  { icon: FileText,     title: "Income Tax\nFiling",      desc: "ITR for Individuals, HUF, Firms, Companies & More" },
  { icon: Receipt,      title: "GST\nCompliance",         desc: "Registration, Returns, Reconciliation & Consultation" },
  { icon: FileText,     title: "TDS / TCS\nCompliance",   desc: "TDS Return Filing, TCS Return & Compliances" },
  { icon: Building2,    title: "Business\nCompliance",    desc: "ROC Filings, Licences, Registrations & Other Compliances" },
  { icon: AlertCircle,  title: "Notice\nAssistance",      desc: "Income Tax, GST Notice Support & Representation" },
  { icon: Briefcase,    title: "Other\nServices",         desc: "PAN, TAN, MSME/Udyam Registration & Other Documentation" },
];

const steps = [
  { n: "01", title: "Submit Your Details",   desc: "Fill our secure intake form with your income sources and personal information. Takes under 3 minutes." },
  { n: "02", title: "Upload Documents",       desc: "Share Form 16, bank statements, and investment proofs securely. We auto-fetch AIS & 26AS from the IT portal." },
  { n: "03", title: "CA Review & E-Filing",  desc: "A qualified CA reviews every deduction, maximises your refund, and e-files. You receive the ITR-V acknowledgment." },
];

const tiers = [
  { name: "Salary Basic", price: "299",   tag: "Single Form 16, straightforward salary.", popular: false, btn: "Get Started",
    items: ["ITR-1 e-filing","Automatic Form 16 fetch","Standard deduction applied","Email support","Filed within 24 hours"] },
  { name: "Salary Pro",   price: "999",   tag: "For multi-income salaried filers.", popular: true, btn: "Choose Pro",
    items: ["ITR-1 / ITR-2 filing","Multiple Form 16 + capital gains","HRA & home loan optimisation","WhatsApp & call support","CA review before filing","Basic IT notice handling"] },
  { name: "Business",     price: "1,499", tag: "For freelancers, consultants & SMEs.", popular: false, btn: "Contact Us",
    items: ["ITR-3 / ITR-4 filing","P&L & balance sheet preparation","GST filings (quarterly)","Dedicated CA contact","Unlimited tax consultation","Advance tax planning"] },
];

const reviews = [
  { name: "Riya Sharma",     role: "Product Designer, Bengaluru",  quote: "Filed on the go — refund credited in 11 days. The CA caught a deduction I had missed for two years." },
  { name: "Arjun Menon",     role: "Freelance Developer, Kochi",   quote: "Switched from my local CA. The fee is a third, turnaround is same-day, and I get responses on WhatsApp." },
  { name: "Nikhil & Saanvi", role: "Co-founders, D2C brand",       quote: "Company ITR, GST, and personal filings handled end-to-end. Exactly what a growing business needs.", wide: true },
  { name: "Priya Iyer",      role: "Senior Consultant, Mumbai",    quote: "Regime switch saved ₹18,000 that my previous CA had overlooked. The attention to detail is exceptional." },
  { name: "Kabir Joshi",     role: "F&O Trader, Pune",             quote: "Capital gains, speculative income, and salary — complex mix, handled without a single follow-up question." },
];

const faqs = [
  { q: "Are you actual CAs or just a software platform?", a: "We are a registered CA firm. Every return is reviewed and digitally signed by a qualified Chartered Accountant before submission. You get professional accountability, not just automated processing." },
  { q: "What documents do I need to get started?", a: "PAN, Aadhaar, Form 16 (salaried), bank statements, capital gains statement (if applicable), and investment proofs (80C, 80D etc). Our intake form prompts you for exactly what your case requires." },
  { q: "How long does the filing process take?", a: "Standard salaried filings are completed within 24 hours of document submission. Complex cases are typically done the same working day after CA review." },
  { q: "Which tax regime should I choose?", a: "It depends on your deduction profile. Our calculator provides an indicative comparison, and the CA assigned to your filing will select the regime that minimises your tax liability — confirmed before filing." },
  { q: "How is my data protected?", a: "All documents are encrypted in transit and at rest. Access is restricted to the CA assigned to your filing. We do not share your data with third parties for any purpose." },
  { q: "What if I receive a notice after filing?", a: "Notice handling is included on Pro and Business plans. We draft the response, compile supporting evidence, and manage correspondence with the IT Department until resolution." },
  { q: "Can you file returns for previous years?", a: "Yes. We file revised and belated returns for the current and the immediately preceding two assessment years." },
  { q: "Do you handle NRI filings?", a: "Yes. We advise on DTAA applicability, foreign income disclosure, FEMA compliance, and repatriation. Our NRI desk handles the full spectrum of cross-border tax matters." },
];

// ────────────────────────────────────────────
// COMPONENTS
// ────────────────────────────────────────────

function TopBar() {
  return (
    <div className="topbar">
      <div className="topbar-inner">
        <div className="topbar-left">
          <span className="topbar-item">📞 +91 7390090042 | +91 7210300900</span>
          <span className="topbar-item">✉️ incometaxbro555@gmail.com</span>
          <span className="topbar-item">🕐 Mon–Sat 10AM–8PM IST</span>
        </div>
        <div className="topbar-right">
          <a href="#faq"     className="topbar-link">Help</a>
          <a href="#contact" className="topbar-link">Contact</a>
          <a href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(WA_MSG)}`} target="_blank" rel="noreferrer" className="topbar-link" style={{color:"#6ee7a0"}}>WhatsApp</a>
        </div>
      </div>
    </div>
  );
}

function Nav() {
  return (
    <nav className="nav">
      <div className="nav-inner">
        {/* Brand */}
        <a href="#top" className="nav-brand">
          <div className="nav-brand-icon">
            <div className="nav-brand-bar"/>
            <div className="nav-brand-bar"/>
            <div className="nav-brand-bar"/>
            <span className="nav-brand-arrow">↗</span>
          </div>
          <div className="nav-brand-text">
            <div className="nav-brand-name">IncomeTax<span>Bro</span></div>
            <div className="nav-brand-sub">TAX EXPERTS <span>•</span> COMPLIANCE PARTNERS</div>
          </div>
        </a>

        {/* Links */}
        <div className="nav-links">
          <a href="#top"        className="nav-link">Home</a>
          <a href="#services"   className="nav-link">Services <ChevronDown size={13}/></a>
          <a href="#how"        className="nav-link">Process</a>
          <a href="#calculator" className="nav-link">Tax Calculator</a>
          <a href="#pricing"    className="nav-link">Fees</a>
          <a href="#faq"        className="nav-link">FAQ</a>
        </div>

        {/* Actions */}
        <div className="nav-actions">
        <Link to="/login" className="btn-login">
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
  Admin Login
</Link>
          <a href="#contact" className="btn-getstarted">
            Get Started <ArrowRight size={15}/>
          </a>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section id="top" className="hero">
      <div className="hero-inner">
        {/* Left content */}
        <div className="hero-content">
          <div className="hero-badge">
            <span className="hero-badge-dot"/>
            AY 2026–27 &nbsp;·&nbsp; ITR Filing Open
          </div>

          <h1 className="hero-h1">
            Expert Tax Filing &<br/>
            <span className="gold">Compliance</span> Services<br/>
            <span className="italic">— for Every Indian —</span>
          </h1>

          <p className="hero-sub">
            IncomeTaxBro provides accurate, timely and confidential tax filing,
            GST compliance and financial solutions for individuals, professionals
            and businesses across India.
          </p>

          <div className="hero-btns">
            <a href="#contact" className="btn-hero-primary">
              Start Your Filing <ArrowRight size={16}/>
            </a>
            <a href="#calculator" className="btn-hero-outline">
              <Calculator size={16}/> Estimate Your Tax
            </a>
          </div>

          <div className="hero-trust">
            {[
              [ShieldCheck, "100% Secure\n& Confidential"],
              [Users,       "Expert Team\nof Professionals"],
              [Zap,         "Fast & Accurate\nFiling"],
              [Award,       "Maximum Refund\nAssistance"],
            ].map(([Icon, text]) => (
              <div key={text} className="hero-trust-item">
                <div className="hero-trust-icon"><Icon size={14}/></div>
                <span style={{whiteSpace:"pre-line",lineHeight:1.3}}>{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Centre — person placeholder (replace with your CA photo) */}
        <div className="hero-person">
          {/* <div className="hero-person-placeholder">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            <span style={{color:"rgba(255,255,255,.25)",fontSize:"12px",textAlign:"center",padding:"0 16px"}}>Add CA photo here</span>
          </div> */}
        </div>

        {/* Right — refund card */}
        <div className="hero-card-wrap">
          <div className="hero-card">
            <div className="hero-card-head">
              <div className="hero-card-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
              </div>
              <span className="hero-card-title">Sample Refund Calculation</span>
            </div>
            {[
              ["Gross Salary",        "₹12,75,000"],
              ["Standard Deduction",  "₹75,000"],
              ["Taxable Income",      "₹12,00,000"],
              ["Tax (New Regime)",    "₹0"],
            ].map(([l, v]) => (
              <div key={l} className="hero-card-row">
                <span className="hero-card-label">{l}</span>
                <span className="hero-card-value">{v}</span>
              </div>
            ))}
            <div className="hero-card-refund">
              <div className="hero-card-refund-label">Estimated Refund</div>
              <div className="hero-card-refund-value">₹42,800</div>
              <div className="hero-card-refund-sub">Amount as per new tax regime</div>
            </div>
            <button className="hero-card-btn1" onClick={() => document.getElementById("contact")?.scrollIntoView({behavior:"smooth"})}>
              File My Return <ArrowRight size={15}/>
            </button>
            <a href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent("Hi! I want to WhatsApp a tax expert.")}`} target="_blank" rel="noreferrer" className="hero-card-btn2">
              <svg width="15" height="15" viewBox="0 0 32 32" fill="#25D366"><path d="M16 2C8.268 2 2 8.268 2 16c0 2.49.647 4.83 1.78 6.86L2 30l7.34-1.74A13.94 13.94 0 0 0 16 30c7.732 0 14-6.268 14-14S23.732 2 16 2z"/></svg>
              WhatsApp a Tax Expert
            </a>
            <div className="hero-card-privacy">
              <ShieldCheck size={12} color="#6b7a8d"/>
              Your data is safe with us. We never share your information with third parties.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const items = ["10,000+ Returns Filed","ISO 27001 Aligned Security","ICAI Registered CA Firm","4.9/5 Google Rating","Same-day Filing Available","PAN-India Service","NRI & DTAA Specialists","Free Regime Comparison"];
  return (
    <div className="trust-bar">
      <div className="trust-track">
        {[...items,...items,...items].map((t, i) => (
          <span key={i} className="trust-item">
            <span className="trust-dot"/> {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function Services() {
  return (
    <section id="services" className="services-wrap">
      <div className="services-outer">
        {/* Left — services grid */}
        <div>
          <div className="sec-header" style={{textAlign:"left"}}>
            <div className="sec-line" style={{justifyContent:"flex-start"}}>
              <div className="sec-line-bar"/>
              <span style={{fontSize:"16px",fontWeight:700,color:"#1a3a5c"}}>Our Services</span>
              <div className="sec-line-bar"/>
            </div>
          </div>
          <div className="services-grid">
            {services.map(s => (
              <div key={s.title} className="svc-card">
                <div className="svc-icon">
                  <s.icon size={24} color="#1a3a5c"/>
                </div>
                <div className="svc-title" style={{whiteSpace:"pre-line"}}>{s.title}</div>
                <div className="svc-desc">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — trust card */}
        <div className="trust-side">
          <div style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:"14px"}}>
            <div style={{width:48,height:48,borderRadius:"50%",background:"rgba(232,184,75,.2)",border:"2px solid rgba(232,184,75,.4)",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Star size={22} color="#e8b84b" fill="#e8b84b"/>
            </div>
            <div>
              <div style={{fontWeight:700,fontSize:"15px",lineHeight:1.3}}>Trusted by Thousands<br/>of Happy Clients</div>
              <div style={{fontSize:"11px",color:"rgba(255,255,255,.5)",marginTop:"2px"}}>Across India</div>
            </div>
          </div>
          <div className="trust-stars">
            {[0,1,2,3,4].map(i => <span key={i} className="trust-star">★</span>)}
          </div>
          <div style={{fontSize:"13px",color:"rgba(255,255,255,.6)",lineHeight:1.6}}>
            Rated 4.9/5 by over 10,000 satisfied taxpayers. Our CA team ensures maximum refunds and zero penalties.
          </div>
          <div className="trust-stats">
            {[["10,000+","Happy Clients"],["7+","Years of Experience"],["100%","Data Protection"]].map(([v,l]) => (
              <div key={l} className="trust-stat">
                <div className="trust-stat-value">{v}</div>
                <div className="trust-stat-label">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom trust features */}
      <div style={{background:"#0d2137",padding:"16px 0",marginTop:"32px"}}>
        <div  style={{maxWidth:"1200px",margin:"0 auto",padding:"0 24px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:"12px"}}>
          {[
            [Award,      "Affordable Pricing",   "Transparent & Genuine Prices"],
            [Users,      "Expert Guidance",      "By Experienced Professionals"],
            [Zap,        "On-Time Filing",       "Avoid Penalties & Notices"],
            [ShieldCheck,"End-to-End Support",   "We're Here to Help You"],
          ].map(([Icon, title, sub]) => (
            <div key={title} className="stickybar-feature-items" style={{display:"flex",alignItems:"center",gap:"10px",color:"white"}}>
              <div style={{width:36,height:36,borderRadius:"50%",background:"rgba(34,160,90,.2)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <Icon size={16} color="#22a05a"/>
              </div>
              <div>
                <div style={{fontWeight:700,fontSize:"13px"}}>{title}</div>
                <div style={{fontSize:"11px",color:"rgba(255,255,255,.55)"}}>{sub}</div>
              </div>
            </div>
          ))}
          <div style={{background:"#22a05a",borderRadius:"8px",padding:"10px 16px",display:"flex",alignItems:"center",gap:"8px",flexShrink:0}}>
            <svg width="16" height="16" viewBox="0 0 32 32" fill="white"><path d="M16 2C8.268 2 2 8.268 2 16c0 2.49.647 4.83 1.78 6.86L2 30l7.34-1.74A13.94 13.94 0 0 0 16 30c7.732 0 14-6.268 14-14S23.732 2 16 2z"/></svg>
            <div>
              <div style={{fontSize:"10px",color:"rgba(255,255,255,.7)"}}>Call / WhatsApp</div>
              <div id="phone_no" style={{fontWeight:800,fontSize:"14px",color:"white"}}><span>+91 7390090042</span> <span id="phone-no-connecter">|</span> <span>+91 7210300900</span></div>
            </div>
            <div style={{borderLeft:"1px solid rgba(255,255,255,.2)",paddingLeft:"10px",fontSize:"11px",color:"rgba(255,255,255,.7)"}}>
              🌐 www.IncomeTaxBro.com
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="how" className="process-wrap">
      <div style={{maxWidth:"1200px",margin:"0 auto",padding:"0 24px 40px"}}>
        <div className="sec-header">
          <div className="sec-eyebrow">Our Process</div>
          <div className="sec-line">
            <div className="sec-line-bar"/>
            <h2 className="sec-title">Simple 3-Step Process</h2>
            <div className="sec-line-bar"/>
          </div>
          <p className="sec-sub">A transparent, structured approach that minimises your effort and maximises accuracy.</p>
        </div>
      </div>
      <div className="process-grid">
        {steps.map(s => (
          <div key={s.n} className="proc-card">
            <div className="proc-num">{s.n}</div>
            <div className="proc-title">{s.title}</div>
            <div className="proc-desc">{s.desc}</div>
          </div>
        ))}
      </div>
      <div style={{maxWidth:"1200px",margin:"32px auto 0",padding:"0 24px",display:"flex",gap:"16px",flexWrap:"wrap",justifyContent:"center"}}>
        {[[ShieldCheck,"256-bit encrypted documents"],[Lock,"Strict data confidentiality"],[Award,"ICAI registered & compliant"],[Zap,"Avg. turnaround: 24 hours"]].map(([Icon,text]) => (
          <div key={text} style={{display:"flex",alignItems:"center",gap:"8px",background:"white",border:"1px solid #e8edf4",borderRadius:"100px",padding:"8px 18px",fontSize:"13px",color:"#6b7a8d",boxShadow:"0 1px 4px rgba(13,33,55,.07)"}}>
            <Icon size={14} color="#c9a14a"/>{text}
          </div>
        ))}
      </div>
    </section>
  );
}

function TaxCalculator() {
  const [regime,     setRegime]     = useState("new");
  const [income,     setIncome]     = useState(1200000);
  const [age,        setAge]        = useState("normal");
  const [deductions, setDeductions] = useState(0);

  const result = useMemo(() => computeTax(income, regime, deductions, age), [income, regime, deductions, age]);

  const incPct = ((income - 100000) / (10000000 - 100000) * 100).toFixed(1);
  const dedPct = (deductions / 500000 * 100).toFixed(1);

  return (
    <section id="calculator" className="calc-wrap">
      <div className="calc-inner">
        <div className="sec-header">
          <div className="sec-eyebrow" style={{color:"#e8b84b"}}>Free Tool · AY 2026-27</div>
          <div className="sec-line">
            <div className="sec-line-bar" style={{background:"#e8b84b"}}/>
            <h2 className="sec-title" style={{color:"white"}}>Income Tax Calculator</h2>
            <div className="sec-line-bar" style={{background:"#e8b84b"}}/>
          </div>
          <p className="sec-sub" style={{color:"rgba(255,255,255,.6)"}}>Accurate slab-by-slab computation for both regimes. Includes 87A rebate, surcharge, and 4% cess.</p>
        </div>

        <div className="calc-grid">
          {/* Controls */}
          <div className="calc-panel">
            <div style={{marginBottom:"18px"}}>
              <div className="calc-row-label">Tax Regime</div>
              <div className="calc-tabs">
                {[["new","New Regime (Default)"],["old","Old Regime"]].map(([k,l]) => (
                  <button key={k} className={`calc-tab${regime===k?" on":""}`} onClick={()=>setRegime(k)}>{l}</button>
                ))}
              </div>
            </div>

            <div style={{marginBottom:"18px"}}>
              <div className="calc-row-label">Age Category</div>
              <div className="age-btns">
                {[["normal","Below 60"],["senior","60–79 (Senior)"],["supersenior","80+ (Super Senior)"]].map(([k,l]) => (
                  <button key={k} className={`age-btn${age===k?" on":""}`} onClick={()=>setAge(k)}>{l}</button>
                ))}
              </div>
            </div>

            <div style={{marginBottom:"16px"}}>
              <div className="sl-header">
                <span className="sl-name">Annual Gross Income</span>
                <span className="sl-value">{fmtINR(income)}</span>
              </div>
              <input type="range" className="calc-range" min={100000} max={10000000} step={50000}
                value={income} style={{"--p":`${incPct}%`}}
                onChange={e=>setIncome(Number(e.target.value))}/>
              <div className="sl-ends"><span>₹1L</span><span>₹1 Crore</span></div>
            </div>

            {regime==="old" && (
              <div style={{marginBottom:"16px"}}>
                <div className="sl-header">
                  <span className="sl-name">Deductions (80C, 80D, HRA…)</span>
                  <span className="sl-value" style={{fontSize:"18px"}}>{fmtINR(deductions)}</span>
                </div>
                <input type="range" className="calc-range" min={0} max={500000} step={5000}
                  value={deductions} style={{"--p":`${dedPct}%`}}
                  onChange={e=>setDeductions(Number(e.target.value))}/>
                <div className="sl-ends"><span>₹0</span><span>₹5L</span></div>
              </div>
            )}

            <div className="calc-meta">
              <div className="calc-meta-box">
                <div className="calc-meta-label">Standard Deduction</div>
                <div className="calc-meta-value">{regime==="new"?"₹75,000":"₹50,000"}</div>
              </div>
              <div className="calc-meta-box">
                <div className="calc-meta-label">87A Rebate Limit</div>
                <div className="calc-meta-value">{regime==="new"?"₹12,00,000":"₹5,00,000"}</div>
              </div>
              {regime==="old" && (
                <div className="calc-meta-box" style={{gridColumn:"1/-1"}}>
                  <div className="calc-meta-label">Basic Exemption ({age==="supersenior"?"80+":age==="senior"?"60–79":"Below 60"})</div>
                  <div className="calc-meta-value">{age==="supersenior"?"₹5,00,000":age==="senior"?"₹3,00,000":"₹2,50,000"}</div>
                </div>
              )}
            </div>

            {/* Slab tables */}
            <div className="slab-tabs">
              <div className="slab-box">
                <div className="slab-box-title">
                  <span className="slab-dot" style={{background:"#e8b84b"}}/>
                  New Regime
                </div>
                <table className="slab-t">
                  <thead><tr><th>Slab</th><th style={{textAlign:"right"}}>Rate</th></tr></thead>
                  <tbody>
                    {[["Up to ₹4,00,000","NIL"],["₹4L – ₹8L","5%"],["₹8L – ₹12L","10%"],["₹12L – ₹16L","15%"],["₹16L – ₹20L","20%"],["₹20L – ₹24L","25%"],["Above ₹24L","30%"]].map(([s,r])=>(
                      <tr key={s}><td>{s}</td><td style={{textAlign:"right"}} className={r==="NIL"?"nil":""}>{r}</td></tr>
                    ))}
                  </tbody>
                </table>
                <div className="slab-note">Std. deduction ₹75,000 · 87A rebate up to ₹12L taxable income</div>
              </div>
              <div className="slab-box">
                <div className="slab-box-title">
                  <span className="slab-dot" style={{background:"#1a3a5c"}}/>
                  Old Regime
                </div>
                <table className="slab-t">
                  <thead><tr><th>Slab</th><th style={{textAlign:"right"}}>Rate</th></tr></thead>
                  <tbody>
                    {[["Up to ₹2,50,000","NIL"],["₹2.5L – ₹5L","5%"],["₹5L – ₹10L","20%"],["Above ₹10L","30%"]].map(([s,r])=>(
                      <tr key={s}><td>{s}</td><td style={{textAlign:"right"}} className={r==="NIL"?"nil":""}>{r}</td></tr>
                    ))}
                  </tbody>
                </table>
                <div className="slab-note">Std. deduction ₹50,000 · 87A rebate up to ₹5L · All 80C/80D/HRA deductions available</div>
              </div>
            </div>
          </div>

          {/* Result */}
          <div className="calc-result-panel">
            <div className="cr-label">Total Tax Liability</div>
            <div className="cr-value">{fmtINR(result.total)}</div>
            <div className="cr-grid">
              <div className="cr-box">
                <div className="cr-box-label">Taxable Income</div>
                <div className="cr-box-value">{fmtINR(result.taxable)}</div>
              </div>
              <div className="cr-box">
                <div className="cr-box-label">Income Tax</div>
                <div className="cr-box-value">{fmtINR(result.tax)}</div>
              </div>
              {result.surcharge > 0 && (
                <div className="cr-box">
                  <div className="cr-box-label">Surcharge</div>
                  <div className="cr-box-value">{fmtINR(result.surcharge)}</div>
                </div>
              )}
              <div className="cr-box">
                <div className="cr-box-label">Cess (4%)</div>
                <div className="cr-box-value">{fmtINR(result.cess)}</div>
              </div>
            </div>
            <div className="cr-rate-box">
              <div className="cr-box-label">Effective Tax Rate</div>
              <div className="cr-box-value">{income>0?((result.total/income)*100).toFixed(2):"0.00"}%</div>
            </div>
            {result.total===0 && income>0 && (
              <div className="cr-zero">✓ Zero Tax — 87A Rebate Applied</div>
            )}
            <button className="cr-cta" onClick={()=>document.getElementById("contact")?.scrollIntoView({behavior:"smooth"})}>
              File with a CA — Optimise This <ArrowRight size={15}/>
            </button>
            <div className="cr-note">Indicative only. Full computation by CA at time of filing.</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section id="pricing" className="pricing-wrap">
      <div style={{maxWidth:"1200px",margin:"0 auto",padding:"0 24px 40px"}}>
        <div className="sec-header">
          <div className="sec-eyebrow">Fee Structure</div>
          <div className="sec-line">
            <div className="sec-line-bar"/>
            <h2 className="sec-title">Transparent, All-Inclusive Fees</h2>
            <div className="sec-line-bar"/>
          </div>
          <p className="sec-sub">No hidden charges. No surprises. Fee covers complete CA-assisted filing.</p>
        </div>
      </div>
      <div className="pricing-grid">
        {tiers.map(t => (
          <div key={t.name} className={`price-card${t.popular?" pop":""}`}>
            {t.popular && <div className="price-pop-badge">Most Popular</div>}
            <div className="price-name">{t.name}</div>
            <div className="price-amount">₹{t.price}<span className="price-per"> / filing</span></div>
            <div className="price-tag">{t.tag}</div>
            <div className="price-div"/>
            <ul className="price-list">
              {t.items.map(item => (
                <li key={item} className="price-item">
                  <span className="price-check"><Check size={10} strokeWidth={3}/></span>
                  {item}
                </li>
              ))}
            </ul>
            <button onClick={()=>document.getElementById("contact")?.scrollIntoView({behavior:"smooth"})}
              className={`price-btn ${t.popular?"price-btn-pop":"price-btn-def"}`}>
              {t.btn} <ArrowRight size={14}/>
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="testi-wrap">
      <div style={{maxWidth:"1200px",margin:"0 auto",padding:"0 24px 40px"}}>
        <div className="sec-header">
          <div className="sec-eyebrow">Client Testimonials</div>
          <div className="sec-line">
            <div className="sec-line-bar"/>
            <h2 className="sec-title">Trusted by 10,000+ Taxpayers Across India</h2>
            <div className="sec-line-bar"/>
          </div>
        </div>
      </div>
      <div className="testi-grid">
        {reviews.map(r => (
          <div key={r.name} className={`testi-card${r.wide?" wide":""}`}>
            <div className="testi-stars">{[0,1,2,3,4].map(i=><span key={i} className="testi-star">★</span>)}</div>
            <p className="testi-q">{r.quote}</p>
            <div className="testi-name">{r.name}</div>
            <div className="testi-role">{r.role}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FAQ() {
  return (
    <section id="faq" className="faq-wrap">
      <div className="faq-inner">
        <div>
          <div className="sec-eyebrow">FAQ</div>
          <div className="faq-side-title">Questions We<br/>Hear Often</div>
          <p className="faq-side-sub">Can't find your question? Speak directly with a CA — no charge for the initial query.</p>
          <a href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent("Hi, I have a question about tax filing.")}`}
             target="_blank" rel="noreferrer" className="faq-wa-btn">
            <svg width="16" height="16" viewBox="0 0 32 32" fill="white"><path d="M16 2C8.268 2 2 8.268 2 16c0 2.49.647 4.83 1.78 6.86L2 30l7.34-1.74A13.94 13.94 0 0 0 16 30c7.732 0 14-6.268 14-14S23.732 2 16 2z"/></svg>
            Ask a CA on WhatsApp
          </a>
        </div>
        <div className="faq-list">
          {faqs.map(f => (
            <details key={f.q} className="faq-item">
              <summary className="faq-summary">
                {f.q}
                <span className="faq-plus">+</span>
              </summary>
              <div className="faq-ans">{f.a}</div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [form, setForm] = useState({full_name:"",mobile:"",email:"",service:"ITR-1 (Salaried)",message:""});
  const [status, setStatus] = useState({state:"idle",msg:""});

  const onChange = e => setForm(f=>({...f,[e.target.name]:e.target.value}));
  const onMobile = e => { const v=e.target.value.replace(/\D/g,""); if(v.length<=10) setForm(f=>({...f,mobile:v})); };

  const submit = async e => {
    e.preventDefault();
    setStatus({state:"loading",msg:""});
    try { await axios.get(`${API}/`,{timeout:60000}); } catch{}
    try {
      const {data} = await axios.post(`${API}/leads`,form,{timeout:60000});
      const name = data.data?.full_name || form.full_name;
      setStatus({state:"ok",msg:`Thank you, ${name.split(" ")[0]}! A CA will contact you within 2 working hours.`});
      setForm({full_name:"",mobile:"",email:"",service:"ITR-1 (Salaried)",message:""});
    } catch(err) {
      setStatus({state:"err",msg:err?.response?.data?.detail?.[0]?.msg||"Submission failed. Please call us directly."});
    }
  };

  return (
    <section id="contact" className="contact-wrap">
      <div className="contact-inner">
        <div>
          <div className="sec-eyebrow" style={{color:"#e8b84b"}}>Get in Touch</div>
          <div className="contact-info-title">Ready to File?<br/>We're Ready to Help.</div>
          <p className="contact-info-sub">Share your details and a CA will reach out within 2 working hours to begin your filing.</p>
          {[
            [Phone, "Phone / WhatsApp", "+91 7390090042 | +91 7210300900"],
            [Mail,  "Email",            "incometaxbro555@gmail.com"],
            [Clock, "Working Hours",    "Mon–Sat · 10:00 AM – 8:00 PM IST"],
          ].map(([Icon,label,value]) => (
            <div key={label} className="ci-item">
              <div className="ci-icon"><Icon size={17}/></div>
              <div>
                <div className="ci-label">{label}</div>
                <div className="ci-value">{value}</div>
              </div>
            </div>
          ))}
          <div className="ci-trust">
            {[[ShieldCheck,"Encrypted & confidential"],[Award,"ICAI registered CA"],[Users,"10,000+ clients"]].map(([Icon,t])=>(
              <div key={t} className="ci-badge"><Icon size={13}/>{t}</div>
            ))}
          </div>
        </div>

        <div className="cf">
          <div className="cf-title">Send Us Your Details</div>
          <form onSubmit={submit} className="cf-grid">
            <div className="cf-group">
              <label className="cf-label">Full Name *</label>
              <input name="full_name" value={form.full_name} onChange={onChange} required className="cf-input" placeholder="Enter Your Full Name"/>
            </div>
            <div className="cf-group">
              <label className="cf-label">Mobile Number *</label>
              <input name="mobile" value={form.mobile} onChange={onMobile} required maxLength={10} className="cf-input" placeholder="Enter Your Mobile Number"/>
            </div>
            <div className="cf-group">
              <label className="cf-label">Email Address</label>
              <input name="email" type="email" value={form.email} onChange={onChange} className="cf-input" placeholder="Enter Your Email Address"/>
            </div>
            <div className="cf-group">
              <label className="cf-label">Service Required</label>
              <select name="service" value={form.service} onChange={onChange} className="cf-input">
                {["ITR-1 (Salaried)","ITR-2 / ITR-3 (Multi-income)","Business ITR-4","GST Filing","TDS Returns","Notice Response","Company Registration","CA Consultation"].map(o=>(
                  <option key={o}>{o}</option>
                ))}
              </select>
            </div>
            <div className="cf-group full">
              <label className="cf-label">Additional Details</label>
              <textarea name="message" value={form.message} onChange={onChange} rows={3} className="cf-input" style={{resize:"none"}} placeholder="Any specific requirements or questions…"/>
            </div>
            <div className="cf-group full">
              <button type="submit" disabled={status.state==="loading"} className="cf-submit">
                {status.state==="loading"?"Submitting…":"Submit Enquiry"} {status.state!=="loading"&&<ArrowRight size={16}/>}
              </button>
              <p className="cf-note">By submitting you agree to our Privacy Policy. We do not share your data with third parties.</p>
            </div>
            {status.state==="ok"  && <div className="cf-ok full">{status.msg}</div>}
            {status.state==="err" && <div className="cf-err full">{status.msg}</div>}
          </form>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-inner">
        <div>
          <div className="footer-brand-name">IncomeTaxBro</div>
          <p className="footer-brand-desc">A registered CA firm providing income tax, GST, and financial advisory services to individuals and businesses across India since 2018.</p>
        </div>
        {[
          ["Services",[["#services","Income Tax Filing"],["#services","GST Compliance"],["#services","TDS Returns"],["#services","Notice Assistance"],["#services","Company Registration"]]],
          ["Company", [["#how","Our Process"],["#pricing","Fee Structure"],["#faq","FAQ"],["#contact","Contact Us"]]],
          ["Legal",   [["#","Privacy Policy"],["#","Terms of Service"],["#","Refund Policy"],["#","Disclaimer"]]],
        ].map(([title,links])=>(
          <div key={title}>
            <div className="footer-col-title">{title}</div>
            {links.map(([href,label])=>(
              <a key={label} href={href} className="footer-link">{label}</a>
            ))}
          </div>
        ))}
      </div>
      <div className="footer-bottom">
        <span>© {new Date().getFullYear()} IncomeTaxBro. All rights reserved.</span>
        <span>ICAI Registered · ISO 27001 Aligned</span>
        <span>www.IncomeTaxBro.com</span>
      </div>
    </footer>
  );
}

function StickyBar() {
  return (
    <div className="sticky-bar">
      <div className="sticky-bar-features">
        {[[Award,"Affordable Pricing"],[Users,"Expert Guidance"],[Zap,"On-Time Filing"],[ShieldCheck,"End-to-End Support"]].map(([Icon,t])=>(
          <div key={t} className="sticky-feat">
            <Icon size={14} className="sticky-feat-icon" color="#22a05a"/>
            {t}
          </div>
        ))}
      </div>
      <div className="sticky-bar-contact">
        <div>
          <div className="sticky-contact-label">Call / WhatsApp</div>
          <div className="sticky-contact-nums">
            <svg width="14" height="14" viewBox="0 0 32 32" fill="#25D366"><path d="M16 2C8.268 2 2 8.268 2 16c0 2.49.647 4.83 1.78 6.86L2 30l7.34-1.74A13.94 13.94 0 0 0 16 30c7.732 0 14-6.268 14-14S23.732 2 16 2z"/></svg>
            7390090042 | 7210300900
          </div>
        </div>
        <div className="sticky-website">
          🌐 www.IncomeTaxBro.com
        </div>
      </div>
      <a href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(WA_MSG)}`} target="_blank" rel="noreferrer" className="sticky-wa-btn">
        <svg width="16" height="16" viewBox="0 0 32 32" fill="white"><path d="M16 2C8.268 2 2 8.268 2 16c0 2.49.647 4.83 1.78 6.86L2 30l7.34-1.74A13.94 13.94 0 0 0 16 30c7.732 0 14-6.268 14-14S23.732 2 16 2z"/></svg>
        WhatsApp Now
      </a>
    </div>
  );
}

function WAFab() {
  const url = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(WA_MSG)}`;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="wa-fab">
      <svg width="18" height="18" viewBox="0 0 32 32" fill="white"><path d="M16 2C8.268 2 2 8.268 2 16c0 2.49.647 4.83 1.78 6.86L2 30l7.34-1.74A13.94 13.94 0 0 0 16 30c7.732 0 14-6.268 14-14S23.732 2 16 2zm0 25.5a11.44 11.44 0 0 1-5.83-1.6l-.42-.25-4.35 1.03 1.06-4.24-.27-.44A11.46 11.46 0 0 1 4.5 16C4.5 9.596 9.596 4.5 16 4.5S27.5 9.596 27.5 16 22.404 27.5 16 27.5zm6.29-8.63c-.34-.17-2.02-.99-2.33-1.1-.31-.12-.54-.17-.77.17-.22.34-.87 1.1-1.07 1.33-.2.22-.39.25-.73.08-.34-.17-1.44-.53-2.74-1.69-1.01-.9-1.7-2.01-1.9-2.35-.2-.34-.02-.52.15-.69.15-.15.34-.39.51-.59.17-.2.22-.34.34-.57.11-.22.06-.42-.03-.59-.08-.17-.77-1.85-1.05-2.53-.28-.67-.56-.58-.77-.59h-.65c-.22 0-.59.08-.9.42-.31.34-1.18 1.15-1.18 2.81s1.21 3.26 1.38 3.49c.17.22 2.38 3.63 5.77 5.09.81.35 1.44.56 1.93.71.81.26 1.55.22 2.13.13.65-.1 2.02-.82 2.3-1.62.28-.8.28-1.48.2-1.62-.08-.14-.31-.22-.65-.39z"/></svg>
      <span>WhatsApp a CA</span>
    </a>
  );
}

function AdBanner() {
  const [show, setShow] = useState(true);
  const waUrl = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent("Hi! I want to know more about your ITR filing offer.")}`;
  if (!show) return null;
  return React.createElement("div",{className:"ad-ov",onClick:()=>setShow(false)},
    React.createElement("div",{className:"ad-modal",onClick:e=>e.stopPropagation()},
      React.createElement("button",{className:"ad-close-btn",onClick:()=>setShow(false)},"×"),
      React.createElement("div",{className:"ad-scroll"},
        React.createElement("a",{href:waUrl,target:"_blank",rel:"noopener noreferrer",onClick:()=>setShow(false),style:{display:"block"}},
          React.createElement("img",{src:randomBanner,alt:"ITR Filing Offer",style:{width:"100%",height:"auto",display:"block"}})
        )
      ),
      React.createElement("a",{href:waUrl,target:"_blank",rel:"noopener noreferrer",onClick:()=>setShow(false),className:"ad-wa-bar"},
        React.createElement("svg",{width:18,height:18,viewBox:"0 0 32 32",fill:"white"},
          React.createElement("path",{d:"M16 2C8.268 2 2 8.268 2 16c0 2.49.647 4.83 1.78 6.86L2 30l7.34-1.74A13.94 13.94 0 0 0 16 30c7.732 0 14-6.268 14-14S23.732 2 16 2zm0 25.5a11.44 11.44 0 0 1-5.83-1.6l-.42-.25-4.35 1.03 1.06-4.24-.27-.44A11.46 11.46 0 0 1 4.5 16C4.5 9.596 9.596 4.5 16 4.5S27.5 9.596 27.5 16 22.404 27.5 16 27.5zm6.29-8.63c-.34-.17-2.02-.99-2.33-1.1-.31-.12-.54-.17-.77.17-.22.34-.87 1.1-1.07 1.33-.2.22-.39.25-.73.08-.34-.17-1.44-.53-2.74-1.69-1.01-.9-1.7-2.01-1.9-2.35-.2-.34-.02-.52.15-.69.15-.15.34-.39.51-.59.17-.2.22-.34.34-.57.11-.22.06-.42-.03-.59-.08-.17-.77-1.85-1.05-2.53-.28-.67-.56-.58-.77-.59h-.65c-.22 0-.59.08-.9.42-.31.34-1.18 1.15-1.18 2.81s1.21 3.26 1.38 3.49c.17.22 2.38 3.63 5.77 5.09.81.35 1.44.56 1.93.71.81.26 1.55.22 2.13.13.65-.1 2.02-.82 2.3-1.62.28-.8.28-1.48.2-1.62-.08-.14-.31-.22-.65-.39z"})
        ),
        "Chat on WhatsApp Now"
      )
    )
  );
}

export default function HomePage() {
  useEffect(()=>{
    const ping=()=>axios.get(`${API}/`).catch(()=>{});
    ping(); const t=setInterval(ping,10*60*1000); return ()=>clearInterval(t);
  },[]);

  return (
    <div style={{paddingBottom:"56px"}}>
      <TopBar/>
      <Nav/>
      <Hero/>
      <TrustBar/>
      <Services/>
      <HowItWorks/>
      <TaxCalculator/>
      <Pricing/>
      <Testimonials/>
      <FAQ/>
      <Contact/>
      <Footer/>
      <StickyBar/>
      <WAFab/>
      <AdBanner/>
    </div>
  );
}